<template>
  <div class="block">
    <div class="mcp_tokens p-4">
      <div class="title text-center mb-4">
        <h1>{{ $t("mcpTokens.title") }}</h1>
        <p class="subtitle mt-2">{{ $t("mcpTokens.subtitle") }}</p>
      </div>

      <!-- Connection info -->
      <div class="info_box mb-4">
        <h3>{{ $t("mcpTokens.connectionTitle") }}</h3>
        <ol class="mt-2">
          <li>{{ $t("mcpTokens.step1") }}</li>
          <li>
            {{ $t("mcpTokens.step2") }}
            <code class="endpoint_url">{{ mcpEndpointUrl }}</code>
            <button class="btn btn_copy" @click="copyToClipboard(mcpEndpointUrl, 'url')">
              <icon icon="Copy" class="copy_icon" />
              <span v-if="copiedField === 'url'">{{ $t("mcpTokens.copied") }}</span>
            </button>
          </li>
          <li>{{ $t("mcpTokens.step3") }}</li>
        </ol>
      </div>

      <!-- Newly created token banner -->
      <div v-if="newToken" class="new_token_box mb-4">
        <h3>{{ $t("mcpTokens.newTokenTitle") }}</h3>
        <p>{{ $t("mcpTokens.newTokenWarning") }}</p>
        <div class="d-flex align-items-center mt-2">
          <input class="form-control token_value" :value="newToken" readonly>
          <button class="btn btn_main ms-2" @click="copyToClipboard(newToken, 'token')">
            <icon icon="Copy" class="copy_icon me-1" />
            <span>{{ copiedField === 'token' ? $t("mcpTokens.copied") : $t("mcpTokens.copy") }}</span>
          </button>
        </div>
        <button class="btn btn_close mt-3" @click="newToken = null">
          {{ $t("mcpTokens.dismiss") }}
        </button>
      </div>

      <!-- Create form -->
      <div class="create_form mb-4">
        <h3>{{ $t("mcpTokens.createTitle") }}</h3>
        <div class="row mt-2">
          <div class="col-md-4 mb-2">
            <label class="form-label">{{ $t("mcpTokens.name") }}</label>
            <input v-model="createForm.name" class="form-control"
              :placeholder="$t('mcpTokens.namePlaceholder')">
          </div>
          <div class="col-md-4 mb-2">
            <label class="form-label">{{ $t("mcpTokens.institution") }}</label>
            <select v-model="createForm.institutionId" class="form-select">
              <option :value="null">{{ $t("mcpTokens.noInstitution") }}</option>
              <option v-for="inst in userInstitutions" :key="inst.id" :value="inst.id">
                {{ inst.name }}
              </option>
            </select>
          </div>
          <div class="col-md-3 mb-2">
            <label class="form-label">{{ $t("mcpTokens.expiresInDays") }}</label>
            <input v-model.number="createForm.expiresInDays" type="number" min="1"
              class="form-control" :placeholder="$t('mcpTokens.expiresPlaceholder')">
          </div>
          <div class="col-md-1 mb-2 d-flex align-items-end">
            <button class="btn btn_main w-100" @click="createToken" :disabled="!createForm.name">
              <icon icon="Plus" class="plus_icon" />
            </button>
          </div>
        </div>
        <div v-if="createError" class="text-danger mt-1">{{ createError }}</div>
      </div>

      <!-- Tokens table -->
      <div class="table_container table-responsive">
        <table class="table table-hover">
          <thead class="table_head">
            <tr>
              <th>{{ $t("mcpTokens.name") }}</th>
              <th>{{ $t("mcpTokens.institution") }}</th>
              <th>{{ $t("mcpTokens.createdAt") }}</th>
              <th>{{ $t("mcpTokens.lastUsedAt") }}</th>
              <th>{{ $t("mcpTokens.expiresAt") }}</th>
              <th>{{ $t("mcpTokens.status") }}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!loading && tokens.length === 0">
              <td colspan="7" class="text-center">{{ $t("mcpTokens.empty") }}</td>
            </tr>
            <tr v-for="t in tokens" :key="t.id" :class="{ revoked_row: !t.is_valid }">
              <td>{{ t.name }}</td>
              <td>
                <span v-if="t.institution_name">{{ t.institution_name }}</span>
                <span v-else class="text-muted">---</span>
              </td>
              <td>{{ formatDate(t.created_at) }}</td>
              <td>
                <span v-if="t.last_used_at">{{ formatDate(t.last_used_at) }}</span>
                <span v-else class="text-muted">---</span>
              </td>
              <td>
                <span v-if="t.expires_at">{{ formatDate(t.expires_at) }}</span>
                <span v-else class="text-muted">---</span>
              </td>
              <td>
                <span v-if="t.revoked_at" class="badge bg-danger">{{ $t("mcpTokens.statusRevoked") }}</span>
                <span v-else-if="!t.is_valid" class="badge bg-warning">{{ $t("mcpTokens.statusExpired") }}</span>
                <span v-else class="badge bg-success">{{ $t("mcpTokens.statusActive") }}</span>
              </td>
              <td>
                <button v-if="t.is_valid" class="btn btn_revoke" @click="revokeToken(t)">
                  {{ $t("mcpTokens.revoke") }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import Icon from '@/components/Icon.vue';
import { CONFIG } from "@/config.js";

export default {
  components: { Icon },
  data() {
    return {
      tokens: [],
      loading: false,
      createForm: {
        name: "",
        institutionId: null,
        expiresInDays: null,
      },
      createError: null,
      newToken: null,
      copiedField: null,
    }
  },
  computed: {
    mcpEndpointUrl() {
      return CONFIG.api.baseUrl.replace(/\/$/, "") + "/mcp/";
    },
    userInstitutions() {
      const user = this.$store.getters.user;
      return (user && user.institutions) ? user.institutions : [];
    },
  },
  mounted() {
    this.loadTokens();
    if (this.$store.getters.selectedInstitution) {
      this.createForm.institutionId = this.$store.getters.selectedInstitution;
    }
  },
  methods: {
    loadTokens() {
      this.loading = true;
      this.$store.getters.api.get("/mcp/tokens")
        .then((response) => { this.tokens = response.data.items || []; })
        .catch(() => { this.tokens = []; })
        .finally(() => { this.loading = false; });
    },
    createToken() {
      this.createError = null;
      const payload = {
        name: this.createForm.name,
        institution_id: this.createForm.institutionId || null,
      };
      if (this.createForm.expiresInDays) {
        payload.expires_in_days = this.createForm.expiresInDays;
      }
      this.$store.getters.api.post("/mcp/tokens/create", payload)
        .then((response) => {
          const item = response.data.item;
          this.newToken = item.token;
          this.createForm.name = "";
          this.createForm.expiresInDays = null;
          this.loadTokens();
        })
        .catch((error) => {
          this.createError = (error.response && error.response.data && error.response.data.error)
            || this.$t("error.undefined");
        });
    },
    revokeToken(token) {
      if (!confirm(this.$t("mcpTokens.confirmRevoke", { name: token.name }))) {
        return;
      }
      this.$store.getters.api.post(`/mcp/tokens/${token.id}/revoke`)
        .then(() => { this.loadTokens(); });
    },
    formatDate(iso) {
      if (!iso) return "";
      const d = new Date(iso);
      return d.toLocaleString();
    },
    copyToClipboard(value, field) {
      navigator.clipboard.writeText(value).then(() => {
        this.copiedField = field;
        setTimeout(() => {
          if (this.copiedField === field) this.copiedField = null;
        }, 2000);
      });
    },
  },
}
</script>

<style scoped lang="scss">
@import "@/scss/abstracts/variables";
@import "@/scss/base/typography.scss";

.mcp_tokens {
  background-color: $white;
  color: $dark-grey;
  border-radius: $radius-lg;
}

.title h1 {
  font-size: clamp(22px, 2.8vw, 35px);
  font-weight: 500;
  font-family: $font-heading;
}

.subtitle {
  color: $dark-grey;
  font-size: 16px;
}

.info_box, .new_token_box, .create_form {
  border: 1px solid $border;
  border-radius: $radius-md;
  background-color: $surface-2;
  padding: 20px;
}

.new_token_box {
  border-color: $purple;
  background-color: lighten($light-purple, 5%);
}

.endpoint_url {
  background-color: $grey;
  padding: 2px 8px;
  border-radius: 4px;
  font-family: monospace;
}

.token_value {
  font-family: monospace;
  font-size: 14px;
}

.btn_copy {
  background: transparent;
  border: none;
  color: $purple;
  padding: 0 6px;

  .copy_icon {
    width: 16px;
    height: 16px;
    stroke: $purple;
  }
}

.copy_icon {
  width: 18px;
  height: 18px;
  stroke: $white;
}

.plus_icon {
  width: 18px;
  height: 18px;
  stroke: $white;
}

// Disabled create button (before a name is typed): keep it clearly visible
// instead of a white icon on a washed-out button.
.create_form .btn_main:disabled {
  background-color: $surface-3;
  box-shadow: none;
  opacity: 1;

  .plus_icon {
    stroke: $text-muted;
  }
}

.btn_revoke {
  background-color: transparent;
  border: 1px solid $purple;
  color: $purple;
  padding: 4px 12px;
  border-radius: $radius-md;
  font-size: 14px;

  &:hover {
    background-color: $purple;
    color: $white;
  }
}

.btn_close {
  background-color: $grey;
  color: $black;
  border-radius: $radius-md;
  padding: 5px 20px;
}

.revoked_row {
  opacity: 0.5;
}

.badge {
  padding: 4px 10px;
  border-radius: $radius-sm;
  font-size: 12px;

  &.bg-success { background-color: #28a745; color: $white; }
  &.bg-danger { background-color: #dc3545; color: $white; }
  &.bg-warning { background-color: #ffc107; color: $black; }
}
</style>
